/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 27 2024
 **/

import { useAtomValue } from "jotai";

import { Icon, Row, TypographyTooltip } from "@/frameworks/designStuff/components";
import { JotaiStore } from "@/frameworks/libraries/commons";

import { colors } from "@/frameworks/core/styles";
import { Avatar } from "@/frameworks/core/components";
import { usePhoneCall, PhoneCallAtoms } from "@/frameworks/core/hooks";

import * as S from "./emotion";
import { usePersistStore } from "@/frameworks/core/persistence";

export default function PhoneGridUser({
	userID,
	statusUserConfirm,
	onUserSelected
}: {
	userID: string;
	statusUserConfirm: boolean;
	onUserSelected: (userID: string, name: string) => void;
}) {
	const userData = usePersistStore((state) => state.users[userID]);
	const phoneCall = usePhoneCall();
	const callList = useAtomValue(PhoneCallAtoms.CallList, { store: JotaiStore });
	const existCallID = Object.keys(callList)?.[0] ?? "";
	const participatedUser = phoneCall.state.participatedUserList(existCallID);

	const isParticipatedCall = participatedUser.length > 1 && participatedUser.includes(userID);

	const color = statusUserConfirm ? colors.accent_00_100 : colors.gradient_40_100_w60;
	return (
		<S.GridUserItemStyled
			backgroundColor={color}
			gap={3}
			key={userID}
			horizonAlign="center"
			selected={statusUserConfirm}
		>
			<S.UserStatusIconWrapper status={statusUserConfirm} horizonAlign="center" verticalAlign="center">
				<Icon
					onClick={() => {
						if (statusUserConfirm || isParticipatedCall) return;
						onUserSelected(userID, userData?.username || "");
					}}
					name={isParticipatedCall ? "Call0224" : statusUserConfirm ? "Check24" : "Close24"}
					size={14}
					fill={colors.neutral_00_100}
				/>
			</S.UserStatusIconWrapper>
			<Avatar id={userID} kind={"people_48"} showMiniProfile={false} />
			<Row horizonAlign="center" css={{ width: "100%", overflow: "hidden" }}>
				<TypographyTooltip.p
					tooltipProps={{ hasIcon: false }}
					componentProps={{
						ellipsis: true,
						maxLine: 1,
						typoColor: "neutral_70_100",
						align: "center",
						typoSize: "Body6"
					}}
				>
					{userData?.name}
				</TypographyTooltip.p>
			</Row>
		</S.GridUserItemStyled>
	);
}
